import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PasswordInput } from '@/components/ui/password-input';
import { LogOut, Users, Building2, Plus, Pencil, Trash2, X, FileText, Newspaper, Receipt, CreditCard, DoorOpen, AlertTriangle, Search, ChevronRight, ChevronLeft, Upload, ClipboardCheck, BarChart3, ShoppingCart, CheckCircle, XCircle, ExternalLink, Package, UserPlus, Eye, Database, Shirt, Sparkles, FileDown } from 'lucide-react';
import RupiahIcon from '@/components/RupiahIcon';
import axios from 'axios';
import { isFinanceAccount, currentAccount } from '@/lib/accountPermissions';
import PropertyManagement from '@/components/admin/PropertyManagement';
import LogViewer from '@/components/admin/LogViewer';
import ContentManagement from '@/components/admin/ContentManagement';
import BillManagement from '@/components/admin/BillManagement';
import MonthlyBilling from '@/components/admin/MonthlyBilling';
import PaymentVerification from '@/components/admin/PaymentVerification';
import CheckoutRequests from '@/components/admin/CheckoutRequests';
import HousekeepingChecklist from '@/components/admin/HousekeepingChecklist';
import KpiOverview from '@/components/admin/KpiOverview';
import TransactionManagement from '@/components/admin/TransactionManagement';
import PurchaseRequests from '@/components/admin/PurchaseRequests';
import Inventory from '@/components/admin/Inventory';
import Laundry from '@/components/admin/Laundry';
import Cleaning from '@/components/admin/Cleaning';
import TenantDatabase from '@/components/admin/TenantDatabase';
import ComplaintSummary from '@/components/ComplaintSummary';
import ComplaintCompletion from '@/components/ComplaintCompletion';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageLightbox = ({ url, label, onClose }) => (
  <div className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center p-4" onClick={onClose}>
    <button onClick={onClose} className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2 hover:bg-black/60">
      <X className="w-5 h-5" />
    </button>
    <p className="text-white text-sm font-medium mb-3 opacity-70">{label}</p>
    <img src={url} alt={label} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
  </div>
);

const DocFileCard = ({ label, field, regId, token }) => {
  const [state, setState] = useState('idle'); // idle | loading | loaded | error | empty
  const [url, setUrl] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);

  const load = () => {
    setState('loading');
    axios.get(`${BACKEND_URL}/api/admin/tenant-registrations/${regId}/image/${field}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data.url) { setUrl(r.data.url); setState('loaded'); }
        else setState('empty');
      })
      .catch(() => setState('error'));
  };

  if (state === 'idle') return (
    <div className="p-2.5 rounded-xl border border-gray-200 bg-gray-50 text-center text-xs">
      <p className="text-isk-muted mb-1.5">{label}</p>
      <button onClick={load} className="inline-flex items-center gap-1 px-2 py-1 bg-isk-teal text-white rounded-full text-xs font-medium hover:bg-isk-dark">
        <Eye className="w-3 h-3" /> Muat Foto
      </button>
    </div>
  );
  if (state === 'loading') return (
    <div className="p-2.5 rounded-xl border border-gray-200 bg-gray-50 text-center text-xs">
      <p className="text-isk-muted mb-1">{label}</p>
      <div className="w-4 h-4 border-2 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto" />
    </div>
  );
  if (state === 'empty') return (
    <div className="p-2.5 rounded-xl border border-red-200 bg-red-50 text-center text-xs">
      <p className="text-isk-muted mb-1">{label}</p>
      <span className="text-red-400">Tidak ada</span>
    </div>
  );
  if (state === 'error') return (
    <div className="p-2.5 rounded-xl border border-orange-200 bg-orange-50 text-center text-xs">
      <p className="text-isk-muted mb-1">{label}</p>
      <button onClick={load} className="text-orange-600 hover:underline font-medium">Gagal — Coba Lagi</button>
    </div>
  );
  const displayUrl = url.startsWith('data:') || url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  const isPdf = url.startsWith('data:application/pdf') || url.toLowerCase().endsWith('.pdf');
  const openPdf = () => {
    try {
      const b64 = displayUrl.split(',')[1];
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      window.open(URL.createObjectURL(new Blob([arr], { type: 'application/pdf' })), '_blank');
    } catch { window.open(displayUrl, '_blank'); }
  };
  return (
    <div className="p-2 rounded-xl border border-isk-teal bg-isk-teal/5 text-center text-xs">
      {!isPdf && showLightbox && <ImageLightbox url={displayUrl} label={label} onClose={() => setShowLightbox(false)} />}
      <p className="text-isk-muted mb-1">{label}</p>
      {isPdf ? (
        <button onClick={openPdf} className="inline-flex items-center gap-1 text-isk-teal font-medium hover:underline">
          <ExternalLink className="w-3 h-3" /> Buka PDF
        </button>
      ) : (
        <div className="cursor-pointer" onClick={() => setShowLightbox(true)}>
          <img src={displayUrl} alt={label} className="w-full h-16 object-cover rounded-lg mb-1" onError={() => setState('error')} />
          <span className="inline-flex items-center gap-1 text-isk-teal font-medium hover:underline">
            <ExternalLink className="w-3 h-3" /> Buka penuh
          </span>
        </div>
      )}
    </div>
  );
};

const COMPLAINT_CATEGORIES = ['Listrik', 'Air', 'AC', 'Furniture', 'Kebersihan', 'Keamanan', 'Lainnya'];
const COMPLAINT_PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-isk-bg text-isk-muted' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'emergency', label: 'Emergency', color: 'bg-red-100 text-red-700' }
];
const COMPLAINT_STATUSES = [
  { value: 'new', label: 'Baru', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'in_progress', label: 'Diproses', color: 'bg-blue-100 text-blue-700' },
  { value: 'closed', label: 'Selesai', color: 'bg-green-100 text-green-700' }
];
const COMPLAINT_ASSIGNMENT_ROLES = [
  { value: 'technician', label: 'Teknisi', color: 'bg-sky-100 text-sky-700' },
  { value: 'housekeeping', label: 'Housekeeping', color: 'bg-isk-teal/10 text-isk-teal' }
];

// Complaint Management Component (inline to avoid babel plugin issues)
export const ComplaintManagement = ({ userRole, userPropertyName }) => {
  const [complaints, setComplaints] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const detailRequest = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [subTab, setSubTab] = useState('aktif');
  const [properties, setProperties] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingUpdates, setPendingUpdates] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [form, setForm] = useState({
    tenant_phone: '', property_name: '', room_number: '',
    category: '', description: '', priority: 'medium',
    preferred_date: '', preferred_time: '', assigned_to_email: '', file: null
  });
  const canCreate = ['superadmin', 'housekeeping', 'technician'].includes(userRole);
  const canSetPriority = ['admin', 'superadmin'].includes(userRole);
  const canSetOwnerCost = ['admin', 'superadmin'].includes(userRole);
  const canAssignTask = ['admin', 'superadmin'].includes(userRole);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const qs = new URLSearchParams();
      if (filterProperty) qs.set('property_name', filterProperty);
      const params = qs.toString() ? `?${qs.toString()}` : '';
      const requests = [
        axios.get(`${BACKEND_URL}/api/admin/complaints${params}`, config),
        axios.get(`${BACKEND_URL}/api/staff/properties`, config),
        axios.get(`${BACKEND_URL}/api/admin/technicians`, config)
      ];
      if (userRole === 'superadmin') {
        requests.push(axios.get(`${BACKEND_URL}/api/admin/active-tenants`, config));
      }
      const responses = await Promise.all(requests);
      const cRes = responses[0];
      const pRes = responses[1];
      const techRes = responses[2];
      const tRes = responses[3];
      setComplaints(cRes.data);
      setProperties(pRes?.data || []);
      setTechnicians(techRes?.data || []);
      setTenants(userRole === 'superadmin' && tRes ? tRes.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filterProperty]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!canCreate) return;
    if (['housekeeping', 'technician'].includes(userRole)) {
      if (!form.property_name || !form.category || !form.description) {
        setError('Lengkapi data wajib'); return;
      }
    } else {
      if (!form.tenant_phone || !form.category || !form.description) {
        setError('Lengkapi data wajib'); return;
      }
    }
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      await axios.post(`${BACKEND_URL}/api/admin/complaints`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setSuccess('Komplain dibuat!');
      setShowModal(false);
      setForm({ tenant_phone: '', property_name: '', room_number: '', category: '', description: '', priority: 'medium', preferred_date: '', preferred_time: '', assigned_to_email: '', file: null });
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.detail || 'Gagal'); }
  };

  const handleUpdate = async (field, value) => {
    setPendingUpdates(count => count + 1);
    try {
      const token = localStorage.getItem('token');
      if (selected?.status === 'closed') return;
      if (!canSetPriority && field === 'priority') return;
      if (!canSetOwnerCost && (field === 'owner_cost' || field === 'owner_cost_description')) return;
      if (!canAssignTask && (field === 'assigned_role' || field === 'assigned_to_email')) return;
      await axios.put(`${BACKEND_URL}/api/admin/complaints/${selected.complaint_id}`, { [field]: value }, { headers: { Authorization: `Bearer ${token}` } });
      const detailRes = await axios.get(`${BACKEND_URL}/api/admin/complaints/${selected.complaint_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(detailRes.data);
      fetchData();
    } catch (err) { setError(err.response?.data?.detail || 'Gagal'); }
    finally { setPendingUpdates(count => count - 1); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setPendingUpdates(count => count + 1);
    try {
      const token = localStorage.getItem('token');
      if (selected?.status === 'closed') return;
      const fd = new FormData();
      fd.append('note', newNote);
      await axios.post(`${BACKEND_URL}/api/admin/complaints/${selected.complaint_id}/notes`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setNewNote('');
      const res = await axios.get(`${BACKEND_URL}/api/admin/complaints/${selected.complaint_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(res.data);
    } catch (err) { setError(err.response?.data?.detail || 'Gagal'); }
    finally { setPendingUpdates(count => count - 1); }
  };

  const openDetail = async (c) => {
    const request = ++detailRequest.current;
    setSelected(c);
    setShowDetail(true);
    setLoadingDetail(true);
    setDetailError('');
    setError('');
    setNewNote('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/complaints/${encodeURIComponent(c.complaint_id)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (request === detailRequest.current) setSelected(res.data);
    } catch {
      if (request === detailRequest.current) setDetailError('Detail komplain gagal dimuat. Silakan coba lagi.');
    } finally {
      if (request === detailRequest.current) setLoadingDetail(false);
    }
  };

  const closeDetail = () => { ++detailRequest.current; setShowDetail(false); };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
  const shortId = (id) => {
    if (!id) return '-';
    return id.length > 10 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;
  };
  const getBadge = (arr, val) => { const x = arr.find(a => a.value === val) || arr[0]; return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${x.color}`}>{x.label}</span>; };

  const filtered = complaints.filter(c => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return c.complaint_id?.toLowerCase().includes(s) || c.tenant_name?.toLowerCase().includes(s) || c.tenant_phone?.includes(s) || c.property_name?.toLowerCase().includes(s) || c.room_number?.toLowerCase().includes(s) || c.category?.toLowerCase().includes(s);
  });
  const aktifList = filtered.filter(c => c.status !== 'closed');
  const selesaiList = filtered.filter(c => c.status === 'closed');
  const displayList = subTab === 'aktif' ? aktifList : selesaiList;

  const selectTenant = (phone) => {
    const t = tenants.find(x => x.tenant_phone === phone);
    setForm({ ...form, tenant_phone: phone, property_name: t?.property_name || '', room_number: t?.room_number || '' });
  };

  if (loading) return <div className="text-center py-12"><div className="w-12 h-12 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="flex gap-4">
      <div className="flex-1 min-w-0 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-isk-text">Komplain</h2>
            <p className="text-xs text-isk-muted mt-0.5">Total {complaints.length} komplain</p>
          </div>
          {canCreate && (
            <Button onClick={() => { setError(''); setForm(f => ({ ...f, property_name: userPropertyName || '' })); setShowModal(true); }} className="bg-isk-teal hover:bg-isk-dark text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" />Tambah Komplain
            </Button>
          )}
        </div>
        {success && <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}
        {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex gap-1 bg-white/40 rounded-xl p-1 flex-wrap">
            <button onClick={() => setSubTab('aktif')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${subTab === 'aktif' ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
              Aktif ({aktifList.length})
            </button>
            <button onClick={() => setSubTab('selesai')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${subTab === 'selesai' ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
              Selesai ({selesaiList.length})
            </button>
          </div>
          <div className="flex-1 min-w-[160px] relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-isk-muted" />
            <Input type="text" placeholder="Cari ID, nama, kamar..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-xs rounded-full border-2" />
          </div>
          <Select value={filterProperty || 'all'} onValueChange={(v) => setFilterProperty(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36 rounded-full text-xs h-8">
              <SelectValue placeholder="Semua Kost" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kost</SelectItem>
              {properties.map((p) => (
                <SelectItem key={`${p.name}-${p.owner_email || ''}`} value={p.name}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="glass-card rounded-2xl overflow-auto max-h-[65vh]">
          {displayList.length === 0 ? (
            <div className="py-12 text-center text-isk-muted text-sm">Tidak ada komplain</div>
          ) : (
            <table className="w-full min-w-max text-xs">
              <thead>
                <tr className="bg-white/60 border-b border-gray-100 sticky top-0 z-10">
                  <th className="px-3 py-2.5 text-left font-medium text-isk-muted">ID</th>
                  <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Kamar</th>
                  <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Deskripsi</th>
                  <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Kategori</th>
                  <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Tanggal</th>
                  <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Prioritas</th>
                  <th className="px-3 py-2.5 text-left font-medium text-isk-muted">Status</th>
                </tr>
              </thead>
              <tbody>
                {displayList.map((c, i) => (
                  <tr key={c.complaint_id || i} onClick={() => openDetail(c)}
                    className="border-b border-gray-50 hover:bg-white/40 cursor-pointer transition-colors">
                    <td className="px-3 py-2.5 font-mono font-bold text-isk-teal whitespace-nowrap" title={c.complaint_id}>
                      <button type="button" aria-label={`Lihat detail ${c.complaint_id}`} onClick={event => { event.stopPropagation(); openDetail(c); }} className="hover:underline focus:underline">{shortId(c.complaint_id)}</button>
                    </td>
                    <td className="px-3 py-2.5 font-medium text-isk-text whitespace-nowrap">
                      {c.property_name ? <span className="text-isk-muted font-normal">{c.property_name.split(' ')[0]} · </span> : ''}{c.room_number || '-'}
                    </td>
                    <td className="px-3 py-2.5 text-isk-muted max-w-[180px]"><p className="truncate">{c.description || '-'}</p></td>
                    <td className="px-3 py-2.5 text-isk-muted capitalize whitespace-nowrap">{c.category || '-'}</td>
                    <td className="px-3 py-2.5 text-isk-muted whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-3 py-2.5">{getBadge(COMPLAINT_PRIORITIES, c.priority)}</td>
                    <td className="px-3 py-2.5">{getBadge(COMPLAINT_STATUSES, c.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={showDetail} onOpenChange={open => { if (!open) closeDetail(); }}>
        <DialogContent className="block w-[calc(100%-2rem)] max-w-3xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-white p-4 sm:p-6">
          <DialogTitle className="pr-6">Detail Komplain — {selected?.complaint_id}</DialogTitle>
          <DialogDescription className="mt-1 mb-4">Informasi lengkap dan penanganan komplain.</DialogDescription>
          {loadingDetail && <p role="status" className="py-8 text-center text-isk-muted">Memuat detail komplain...</p>}
          {detailError && <div role="alert" className="p-3 text-sm bg-red-50 text-red-700 rounded-lg">{detailError}<Button variant="outline" onClick={() => openDetail(selected)} className="ml-2">Coba Lagi</Button></div>}
          {!loadingDetail && !detailError && selected && <>
          {error && <p role="alert" className="mb-3 p-3 bg-red-50 text-red-700 rounded-lg">{error}</p>}
          {selected.status === 'closed' && (
            <div className="mb-4 p-3 rounded-lg bg-isk-gold/10 border border-isk-gold/30 text-isk-text text-sm">
              Komplain sudah selesai dan tidak bisa diubah.
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-white/70 border border-white/40">
              <Label className="text-xs text-isk-muted">Prioritas</Label>
              {canSetPriority ? (
                <Select value={selected.priority} onValueChange={(v) => handleUpdate('priority', v)} disabled={selected.status === 'closed'}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMPLAINT_PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div className="mt-2">{getBadge(COMPLAINT_PRIORITIES, selected.priority)}</div>
              )}
            </div>
          </div>
          <div className="mb-4 p-3 rounded-lg bg-white/70 border border-white/40">
            <Label className="text-xs text-isk-muted">Job Desk</Label>
            {canAssignTask ? (
              <Select
                value={selected.assigned_to_email || 'unassigned'}
                onValueChange={(v) => handleUpdate('assigned_to_email', v === 'unassigned' ? '' : v)}
                disabled={selected.status === 'closed'}
              >
                <SelectTrigger className="mt-1 rounded-lg"><SelectValue placeholder="Pilih Staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Belum diassign</SelectItem>
                  {technicians.filter(t => t.role === 'technician').length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-isk-muted uppercase tracking-wide">Teknisi</div>
                      {technicians.filter(t => t.role === 'technician').map((t) => (
                        <SelectItem key={t.email} value={t.email}>{t.name}</SelectItem>
                      ))}
                    </>
                  )}
                  {technicians.filter(t => t.role === 'housekeeping').length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-isk-muted uppercase tracking-wide">Housekeeping</div>
                      {technicians.filter(t => t.role === 'housekeeping').map((t) => (
                        <SelectItem key={t.email} value={t.email}>{t.name}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-2">
                {selected.assigned_to_name
                  ? <span className="text-sm font-medium text-isk-text">{selected.assigned_to_name}</span>
                  : <span className="text-sm text-isk-muted">Belum diassign</span>}
              </div>
            )}
            {(selected.assigned_by || selected.assigned_at) && (
              <div className="mt-2 text-xs text-isk-muted">
                {selected.assigned_by && <div>Diassign oleh: {selected.assigned_by}</div>}
                {selected.assigned_at && <div>Pada: {fmtDateTime(selected.assigned_at)}</div>}
              </div>
            )}
          </div>
          <ComplaintSummary complaint={selected} showProgress={false}
            photoUploadUrl={canSetOwnerCost ? `${BACKEND_URL}/api/admin/complaints/${encodeURIComponent(selected.complaint_id)}/photo` : null}
            onPhotoUploading={setPhotoUploading} photoToken={localStorage.getItem('token')} onPhotoUpdated={data => setSelected(current => ({ ...current, ...data }))} />
          <div className="mb-4 p-3 rounded-lg bg-white/70 border border-white/40">
            <Label className="text-xs text-isk-muted">Estimasi Pengerjaan</Label>
            {userRole === 'technician' ? (
              <p className="text-sm text-isk-teal mt-1">
                {selected.estimated_date ? fmtDate(selected.estimated_date) : '-'} {selected.estimated_time || ''}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input type="date" value={selected.estimated_date || ''} onChange={(e) => handleUpdate('estimated_date', e.target.value)} className="rounded-lg text-sm" disabled={selected.status === 'closed'} />
                <Input type="time" value={selected.estimated_time || ''} onChange={(e) => handleUpdate('estimated_time', e.target.value)} className="rounded-lg text-sm" disabled={selected.status === 'closed'} />
              </div>
            )}
          </div>
          {(selected.status === 'in_progress' || selected.internal_notes?.length > 0) && (
            <div className="mb-4 p-3 rounded-lg bg-white/70 border border-white/40">
              <Label className="text-xs text-isk-muted">Catatan Internal</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {selected.internal_notes?.length > 0 ? selected.internal_notes.map((n, i) => (
                  <div key={i} className="p-2 rounded-lg bg-white/60 border border-white/40 text-sm">
                    <p className="whitespace-pre-wrap break-words">{n.note}</p>
                    <p className="text-xs text-isk-muted mt-1">{n.created_by_name} - {fmtDateTime(n.created_at)}</p>
                  </div>
                )) : <p className="text-xs text-isk-muted">Belum ada catatan</p>}
              </div>
              <div className="flex gap-2 mt-2">
                <Input type="text" placeholder="Tambah catatan..." value={newNote} onChange={(e) => setNewNote(e.target.value)} className="rounded-lg text-sm" disabled={selected.status === 'closed'} />
                <Button onClick={handleAddNote} size="sm" className="bg-isk-teal text-white rounded-lg" disabled={selected.status === 'closed'}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
          {canSetOwnerCost && (
            <div className="p-3 rounded-lg bg-orange-50 border border-orange-200"><Label className="text-xs text-orange-700">Biaya Owner (Jika Ada)</Label>
              <Input type="number" placeholder="Rp 0" value={selected.owner_cost || ''} onChange={(e) => handleUpdate('owner_cost', parseFloat(e.target.value) || null)} className="mt-1 rounded-lg text-sm" disabled={selected.status === 'closed'} />
              <Input type="text" placeholder="Keterangan biaya..." value={selected.owner_cost_description || ''} onChange={(e) => handleUpdate('owner_cost_description', e.target.value)} className="mt-2 rounded-lg text-sm" disabled={selected.status === 'closed'} />
            </div>
          )}
          <ComplaintCompletion key={selected.complaint_id} complaint={selected} busy={pendingUpdates > 0 || photoUploading}
            onComplete={data => { setSelected(current => current?.complaint_id === selected.complaint_id ? ({ ...current, ...data }) : current); setSuccess('Komplain berhasil diselesaikan.'); fetchData(); }} />
          <Button
            onClick={closeDetail}
            className="w-full mt-4 bg-isk-teal hover:bg-isk-dark text-white rounded-full"
          >
            Tutup
          </Button>
          </>}
        </DialogContent>
      </Dialog>

      {showModal && canCreate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="glass-card rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Tambah Komplain</h3>
              <button onClick={() => setShowModal(false)} className="text-isk-muted hover:text-isk-text"><X className="w-6 h-6" /></button>
            </div>
            {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
            <form onSubmit={handleCreate} className="space-y-4">
              {['housekeeping', 'technician'].includes(userRole) ? (
                <>
                  <div><Label>Properti</Label>
                    {userPropertyName ? (
                      <Input value={userPropertyName} readOnly className="mt-1 rounded-xl bg-gray-50" />
                    ) : (
                      <Select value={form.property_name} onValueChange={(v) => setForm({ ...form, property_name: v })}>
                        <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih properti" /></SelectTrigger>
                        <SelectContent>{properties.map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nomor Kamar</Label><Input value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} placeholder="(opsional)" className="mt-1 rounded-xl" /></div>
                    <div><Label>Lokasi Detail</Label><Input value={form.location_detail || ''} onChange={(e) => setForm({ ...form, location_detail: e.target.value })} placeholder="(opsional)" className="mt-1 rounded-xl" /></div>
                  </div>
                </>
              ) : (
                <>
                  <div><Label>Pilih Tenant *</Label>
                    <Select value={form.tenant_phone} onValueChange={selectTenant}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih tenant" /></SelectTrigger>
                      <SelectContent>{tenants.map((t, i) => <SelectItem key={i} value={t.tenant_phone}>{t.tenant_name} - {t.property_name} ({t.room_number})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {form.tenant_phone && <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20"><p className="text-sm font-medium">{form.property_name}</p><p className="text-xs text-isk-muted">Kamar {form.room_number}</p></div>}
                </>
              )}
              <div><Label>Kategori *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent>{COMPLAINT_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Deskripsi *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Jelaskan masalah..." className="mt-1 rounded-xl" rows={3} /></div>
              <div><Label>Upload Foto</Label>
                <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="hidden" id="complaint-photo-admin" />
                <label htmlFor="complaint-photo-admin" className="mt-1 flex items-center justify-center w-full h-24 border-2 border-dashed border-isk-teal/30 rounded-xl cursor-pointer hover:bg-isk-teal/5">
                  {form.file ? <p className="text-sm text-isk-teal">{form.file.name}</p> : <div className="text-center"><Upload className="w-6 h-6 text-isk-muted mx-auto mb-1" /><p className="text-xs text-isk-muted">Klik untuk upload</p></div>}
                </label>
              </div>
              <div><Label>Prioritas</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMPLAINT_PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!['housekeeping', 'technician'].includes(userRole) && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Tanggal Preferensi</Label><Input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="mt-1 rounded-xl" /></div>
                    <div><Label>Jam Preferensi</Label><Input type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="mt-1 rounded-xl" /></div>
                  </div>
                  <div><Label>Job Desk</Label>
                    <Select value={form.assigned_to_email || 'unassigned'} onValueChange={(v) => setForm({ ...form, assigned_to_email: v === 'unassigned' ? '' : v })}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Pilih Staff" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Belum diassign</SelectItem>
                        {technicians.filter(t => t.role === 'technician').length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-isk-muted uppercase tracking-wide">Teknisi</div>
                            {technicians.filter(t => t.role === 'technician').map(t => <SelectItem key={t.email} value={t.email}>{t.name}</SelectItem>)}
                          </>
                        )}
                        {technicians.filter(t => t.role === 'housekeeping').length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-isk-muted uppercase tracking-wide">Housekeeping</div>
                            {technicians.filter(t => t.role === 'housekeeping').map(t => <SelectItem key={t.email} value={t.email}>{t.name}</SelectItem>)}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="flex space-x-3 pt-4">
                <Button type="button" onClick={() => setShowModal(false)} variant="outline" className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full">Simpan</Button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [activeTab, setActiveTab] = useState('properties');
  const [billSubTab, setBillSubTab] = useState('tagihan');
  const tabsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scrollTabs = (dir) => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    name: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const canSeeTab = (tab) => (allowedTabsByRole[userRole] || []).includes(tab);
  const panelLabel = 'Dashboard';

  const allowedTabsByRole = {
    superadmin: ['owners', 'properties', 'transactions', 'kpi', 'bills', 'verification', 'checkout', 'complaints', 'content', 'requests', 'inventory', 'laundry', 'cleaning', 'logs', 'registrations', 'tenant-db'],
    admin: ['properties', 'transactions', 'bills', 'checkout', 'complaints', 'content', 'requests', 'inventory', 'laundry', 'cleaning', 'tenant-db'],
    technician: ['complaints', 'requests', 'inventory'],
    housekeeping: ['housekeeping', 'complaints', 'requests', 'inventory'],
    laundry: ['laundry', 'complaints', 'requests', 'inventory']
  };
  const visibleTabs = allowedTabsByRole[userRole] || [];
  const singleTabView = visibleTabs.length === 1;
  const mobileTabLabelClass = singleTabView ? 'inline' : 'hidden sm:inline';

  useEffect(() => {
    const token = localStorage.getItem('token');
    const ownerData = localStorage.getItem('owner');
    
    if (!token || !ownerData) {
      navigate('/login');
      return;
    }

    const parsedOwner = JSON.parse(ownerData);
    const allowedRoles = ['admin', 'superadmin', 'technician', 'housekeeping', 'laundry'];
    if (!allowedRoles.includes(parsedOwner.role)) {
      navigate('/dashboard');
      return;
    }

    setAdmin(parsedOwner);
    setUserRole(parsedOwner.role);

    // Restore last active tab (superadmin should return to last tab, not always owners)
    const roleTabs = allowedTabsByRole[parsedOwner.role] || [];
    const savedTab = localStorage.getItem(`admin_active_tab_${parsedOwner.role}`);
    if (savedTab && roleTabs.includes(savedTab)) {
      setActiveTab(savedTab);
    } else {
      setActiveTab(roleTabs[0] || 'properties');
    }
    
    if (parsedOwner.role === 'superadmin') {
      fetchOwners(token);
    } else {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!userRole || !activeTab) return;
    const roleTabs = allowedTabsByRole[userRole] || [];
    if (roleTabs.includes(activeTab)) {
      localStorage.setItem(`admin_active_tab_${userRole}`, activeTab);
    }
  }, [activeTab, userRole]);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    checkScroll();
    const obs = new ResizeObserver(checkScroll);
    obs.observe(el);
    return () => obs.disconnect();
  }, [checkScroll, userRole]);

  const fetchOwners = async (token) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${BACKEND_URL}/api/admin/owners`, config);
      setOwners(response.data);
    } catch (error) {
      console.error('Error fetching owners:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('owner');
    navigate('/login');
  };

  const handleOpenModal = (mode, owner = null) => {
    setModalMode(mode);
    setSelectedOwner(owner);
    if (mode === 'edit' && owner) {
      setFormData({
        email: owner.email,
        phone: owner.phone,
        name: owner.name,
        password: ''
      });
    } else {
      setFormData({ email: '', phone: '', name: '', password: '' });
    }
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ email: '', phone: '', name: '', password: '' });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (modalMode === 'create') {
        await axios.post(`${BACKEND_URL}/api/admin/owners`, formData, config);
        setSuccess('Owner berhasil ditambahkan!');
      } else {
        const updateData = { ...formData };
        delete updateData.email;
        if (!updateData.password) delete updateData.password;
        
        await axios.put(`${BACKEND_URL}/api/admin/owners/${selectedOwner.email}`, updateData, config);
        setSuccess('Owner berhasil diupdate!');
      }

      fetchOwners(token);
      setTimeout(() => handleCloseModal(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (ownerEmail) => {
    if (isFinanceAccount(admin)) return;
    if (!window.confirm(`Hapus owner ${ownerEmail}? Semua data properti dan transaksi akan ikut terhapus.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${BACKEND_URL}/api/admin/owners/${ownerEmail}`, config);
      setSuccess('Owner berhasil dihapus!');
      fetchOwners(token);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Gagal menghapus owner');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-isk-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-isk-muted">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-isk-bg">
      {/* Header - Fixed at top */}
      <div className="glass-nav border-b border-white/20 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <h1 className="text-lg sm:text-2xl font-bold text-isk-teal">ISK</h1>
              <span className="text-isk-muted hidden sm:inline">|</span>
              <span className="text-isk-text font-medium text-sm sm:text-base hidden sm:inline">{panelLabel}</span>
            </div>
            <div className="sm:hidden mt-1">
              <p className="text-xs font-semibold text-isk-text truncate">{panelLabel}</p>
              <p className="text-[11px] text-isk-muted truncate">{admin?.name}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-isk-text">{admin?.name}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-2 border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
        
        {/* Tabs - Fixed below header */}
        <div className="border-t border-white/10 bg-isk-bg/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="relative flex items-center">
              {/* Left arrow */}
              {canScrollLeft && (
                <button onClick={() => scrollTabs(-1)}
                  className="absolute left-0 z-10 h-full px-1 flex items-center bg-gradient-to-r from-isk-bg/90 to-transparent pr-4">
                  <div className="w-7 h-7 rounded-full bg-white/80 shadow flex items-center justify-center hover:bg-white transition-colors">
                    <ChevronLeft className="w-4 h-4 text-isk-text" />
                  </div>
                </button>
              )}
              {/* Right arrow */}
              {canScrollRight && (
                <button onClick={() => scrollTabs(1)}
                  className="absolute right-0 z-10 h-full px-1 flex items-center bg-gradient-to-l from-isk-bg/90 to-transparent pl-4">
                  <div className="w-7 h-7 rounded-full bg-white/80 shadow flex items-center justify-center hover:bg-white transition-colors">
                    <ChevronRight className="w-4 h-4 text-isk-text" />
                  </div>
                </button>
              )}
            <div
              ref={tabsRef}
              onScroll={checkScroll}
              className={`flex py-2 overflow-x-auto scrollbar-hide scroll-smooth ${singleTabView ? 'justify-start' : 'space-x-2 sm:space-x-3'}`}
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {canSeeTab('housekeeping') && (
            <button
              onClick={() => setActiveTab('housekeeping')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'housekeeping'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-housekeeping"
            >
              <ClipboardCheck className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Checklist Housekeeping</span>
            </button>
          )}

              {/* Owner tab */}
              {canSeeTab('owners') && (
                <button
                  onClick={() => setActiveTab('owners')}
                  className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                    activeTab === 'owners'
                      ? 'bg-isk-teal text-white shadow-lg'
                      : 'bg-white/60 text-isk-text hover:bg-white/80'
                  }`}
                  data-testid="tab-owners"
                >
              <Users className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Owners</span>
            </button>
          )}
          {canSeeTab('properties') && (
            <button
              onClick={() => setActiveTab('properties')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'properties'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-properties"
            >
              <Building2 className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Properti</span>
            </button>
          )}

          {canSeeTab('transactions') && (
            <button
              onClick={() => setActiveTab('transactions')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'transactions'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-transactions"
            >
              <RupiahIcon className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Transaksi</span>
            </button>
          )}

          {canSeeTab('kpi') && (
            <button
              onClick={() => setActiveTab('kpi')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'kpi'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-kpi"
            >
              <BarChart3 className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>KPI</span>
            </button>
          )}
          
          {/* Bills tab */}
          {canSeeTab('bills') && (
            <button
              onClick={() => setActiveTab('bills')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'bills'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-bills"
            >
              <Receipt className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Tagihan</span>
            </button>
          )}
          
          {/* Payment Verification tab */}
          {canSeeTab('verification') && (
            <button
              onClick={() => setActiveTab('verification')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'verification'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-verification"
            >
              <CreditCard className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Verifikasi</span>
            </button>
          )}
          
          {/* Checkout Requests tab */}
          {canSeeTab('checkout') && (
            <button
              onClick={() => setActiveTab('checkout')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'checkout'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-checkout"
            >
              <DoorOpen className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Checkout</span>
            </button>
          )}
          
          {/* Complaints tab */}
          {canSeeTab('complaints') && (
            <button
              onClick={() => setActiveTab('complaints')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'complaints'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-complaints"
            >
              <AlertTriangle className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Komplain</span>
            </button>
          )}

          {canSeeTab('laundry') && (
            <button
              onClick={() => setActiveTab('laundry')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'laundry'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
            >
              <Shirt className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Laundry</span>
            </button>
          )}

          {canSeeTab('cleaning') && (
            <button
              onClick={() => setActiveTab('cleaning')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'cleaning'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
            >
              <Sparkles className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Pembersihan</span>
            </button>
          )}

          {canSeeTab('requests') && (
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'requests'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
            >
              <ShoppingCart className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Pengajuan</span>
            </button>
          )}

          {canSeeTab('inventory') && (
            <button
              onClick={() => setActiveTab('inventory')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'inventory'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
            >
              <Package className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Inventory</span>
            </button>
          )}

          {/* Content tab (placed left of Log) */}
          {canSeeTab('content') && (
            <button
              onClick={() => setActiveTab('content')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'content'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-content"
            >
              <Newspaper className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Konten</span>
            </button>
          )}

          {/* Log tab - placed at far right */}
          {canSeeTab('logs') && (
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'logs'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
              data-testid="tab-logs"
            >
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Log</span>
            </button>
          )}
          {canSeeTab('registrations') && (
            <button
              onClick={() => setActiveTab('registrations')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'registrations'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
            >
              <UserPlus className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Pendaftaran</span>
            </button>
          )}
          {canSeeTab('tenant-db') && (
            <button
              onClick={() => setActiveTab('tenant-db')}
              className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-sm ${
                activeTab === 'tenant-db'
                  ? 'bg-isk-teal text-white shadow-lg'
                  : 'bg-white/60 text-isk-text hover:bg-white/80'
              }`}
            >
              <Database className="w-4 h-4 sm:mr-2" />
              <span className={`${mobileTabLabelClass} ${singleTabView ? 'ml-2' : ''}`}>Data Tenant</span>
            </button>
          )}
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - with padding for fixed header */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
            {success}
          </div>
        )}
        {error && !showModal && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
            {error}
          </div>
        )}

        {/* Owners Tab */}
        {activeTab === 'owners' && (
          <div>
            {/* Header Actions */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl sm:text-3xl font-bold text-isk-text">Kelola Owner</h2>
                <p className="text-isk-muted mt-1 text-sm">Total {owners.length} owner terdaftar</p>
              </div>
              <Button
                onClick={() => handleOpenModal('create')}
                className="bg-isk-teal hover:bg-isk-dark text-white rounded-full px-6 py-3 font-semibold shadow-lg"
            data-testid="add-owner-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Tambah Owner
          </Button>
        </div>

        {/* Owners Table */}
        <div className="glass-card rounded-2xl p-8">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="owners-table">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-4 px-4 text-sm font-semibold text-isk-text">Nama</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-isk-text">Email</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-isk-text">Telepon</th>
                  <th className="text-center py-4 px-4 text-sm font-semibold text-isk-text">Properti</th>
                  <th className="text-center py-4 px-4 text-sm font-semibold text-isk-text">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {owners.map((owner, index) => (
                  <tr key={index} className="border-b border-white/10 hover:bg-white/30 transition-colors">
                    <td className="py-4 px-4 text-sm text-isk-text font-medium">{owner.name}</td>
                    <td className="py-4 px-4 text-sm text-isk-muted">{owner.email}</td>
                    <td className="py-4 px-4 text-sm text-isk-muted">{owner.phone}</td>
                    <td className="py-4 px-4 text-sm text-center">
                      <span className="px-3 py-1 rounded-full bg-isk-teal/10 text-isk-teal font-semibold">
                        {owner.properties_count}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center space-x-2">
                        <Button
                          onClick={() => handleOpenModal('edit', owner)}
                          variant="outline"
                          size="sm"
                          className="border-isk-gold/30 text-isk-gold hover:bg-isk-gold hover:text-white"
                          data-testid={`edit-owner-${index}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!isFinanceAccount(admin) && <Button
                          onClick={() => handleDelete(owner.email)}
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white"
                          data-testid={`delete-owner-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && canSeeTab('properties') && (
          <PropertyManagement userRole={userRole} account={admin} />
        )}

        {activeTab === 'transactions' && canSeeTab('transactions') && (
          <TransactionManagement userRole={userRole} />
        )}

        {activeTab === 'kpi' && canSeeTab('kpi') && (
          <KpiOverview />
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && canSeeTab('logs') && (
          <LogViewer />
        )}

        {/* Content Tab */}
        {activeTab === 'content' && canSeeTab('content') && (
          <ContentManagement />
        )}

        {/* Bills Tab */}
        {activeTab === 'bills' && canSeeTab('bills') && (
          <div className="space-y-4">
            <div className="flex gap-1 bg-white/40 rounded-xl p-1 w-fit">
              {[['tagihan','Tagihan'],['tagihan-bulanan','Tagihan Bulanan']].map(([key,label]) => (
                <button key={key} onClick={() => setBillSubTab(key)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${billSubTab===key ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
                  {label}
                </button>
              ))}
            </div>
            {billSubTab === 'tagihan' && <BillManagement userRole={userRole} />}
            {billSubTab === 'tagihan-bulanan' && <MonthlyBilling userRole={userRole} />}
          </div>
        )}

        {activeTab === 'verification' && canSeeTab('verification') && (
          <PaymentVerification userRole={userRole} />
        )}

        {activeTab === 'checkout' && canSeeTab('checkout') && (
          <CheckoutRequests />
        )}

        {activeTab === 'complaints' && canSeeTab('complaints') && (
          <ComplaintManagement userRole={userRole} userPropertyName={admin?.property_name || null} />
        )}

        {activeTab === 'housekeeping' && canSeeTab('housekeeping') && (
          <HousekeepingChecklist userRole={userRole} />
        )}
        {activeTab === 'laundry' && canSeeTab('laundry') && (
          <Laundry userRole={userRole} userPropertyName={admin?.property_name || null} />
        )}
        {activeTab === 'cleaning' && canSeeTab('cleaning') && (
          <Cleaning userRole={userRole} userPropertyName={admin?.property_name || null} />
        )}
        {activeTab === 'requests' && canSeeTab('requests') && (
          <PurchaseRequests userRole={userRole} adminEmail={admin?.email} adminName={admin?.name} userPropertyName={admin?.property_name} />
        )}

        {activeTab === 'inventory' && canSeeTab('inventory') && (
          <Inventory userRole={userRole} />
        )}
        {activeTab === 'registrations' && canSeeTab('registrations') && (
          <TenantRegistrations userRole={userRole} />
        )}
        {activeTab === 'tenant-db' && canSeeTab('tenant-db') && (
          <TenantDatabase userRole={userRole} account={admin} />
        )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
          <div className="glass-card rounded-2xl p-8 max-w-md w-full" data-testid="owner-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-isk-text">
                {modalMode === 'create' ? 'Tambah Owner Baru' : 'Edit Owner'}
              </h3>
              <button onClick={handleCloseModal} className="text-isk-muted hover:text-isk-text">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nama Lengkap *</Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="modal-input-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  disabled={modalMode === 'edit'}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl disabled:opacity-50"
                  data-testid="modal-input-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Nomor Telepon *</Label>
                <Input
                  id="phone"
                  type="tel"
                  required={modalMode === 'create'}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="modal-input-phone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  Password {modalMode === 'create' ? '*' : '(kosongkan jika tidak diubah)'}
                </Label>
                <PasswordInput
                  id="password"
                  required={modalMode === 'create'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="modal-input-password"
                  minLength={6}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={handleCloseModal}
                  variant="outline"
                  className="flex-1 border-2 border-isk-teal/30 text-isk-text rounded-full"
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-isk-teal hover:bg-isk-dark text-white rounded-full font-semibold"
                  data-testid="modal-submit-button"
                >
                  {modalMode === 'create' ? 'Tambah Owner' : 'Simpan Perubahan'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const TenantRegistrations = ({ userRole }) => userRole === 'superadmin' ? <SuperadminTenantRegistrations /> : null;

const SuperadminTenantRegistrations = () => {
  const canVerify = currentAccount()?.role === 'superadmin';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState('');
  const [verifyData, setVerifyData] = useState({ property_name: '', room_number: '', room_price: '', deposit_amount: '', deposit_date: '', deposit_luar_admin: false, laundry: false, laundry_price: '', cleaning: false, cleaning_price: '', parkir: false, parkir_price: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loadingAgreement, setLoadingAgreement] = useState('');
  const [agreementPreview, setAgreementPreview] = useState(null);
  const agreementPreviewUrlRef = useRef(null);

  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' }) : '-';
  const fmtN = (v) => v ? Number(String(v).replace(/\./g, '')).toLocaleString('id-ID') : '';
  const rawN = (v) => String(v).replace(/[^0-9]/g, '');

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/admin/tenant-registrations`, cfg()),
      axios.get(`${BACKEND_URL}/api/staff/properties`, cfg()),
    ]).then(([r, p]) => { setRegs(r.data); setProperties(p.data); }).finally(() => setLoading(false));
  }, []);

  const refresh = () => axios.get(`${BACKEND_URL}/api/admin/tenant-registrations`, cfg()).then(r => setRegs(r.data));

  useEffect(() => () => {
    if (agreementPreviewUrlRef.current) URL.revokeObjectURL(agreementPreviewUrlRef.current);
  }, []);

  const closeAgreementPreview = () => {
    if (agreementPreviewUrlRef.current) {
      URL.revokeObjectURL(agreementPreviewUrlRef.current);
      agreementPreviewUrlRef.current = null;
    }
    setAgreementPreview(null);
  };

  const openAgreementPreview = async reg => {
    setError('');
    setLoadingAgreement(reg.reg_id);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/tenant-registrations/${encodeURIComponent(reg.reg_id)}/agreement`,
        { ...cfg(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (agreementPreviewUrlRef.current) URL.revokeObjectURL(agreementPreviewUrlRef.current);
      agreementPreviewUrlRef.current = url;
      setAgreementPreview({ url, regId: reg.reg_id, tenantName: reg.nama });
    } catch (e) {
      setError(e.response?.data?.detail || 'Preview perjanjian gagal dimuat');
    } finally {
      setLoadingAgreement('');
    }
  };

  const downloadAgreement = () => {
    if (!agreementPreview) return;
    const link = document.createElement('a');
    link.href = agreementPreview.url;
    link.download = `Perjanjian-${agreementPreview.regId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openAction = (reg, act) => {
    setSelected(reg); setAction(act); setError('');
    const defaultPeriod = reg.tanggal_mulai_sewa ? reg.tanggal_mulai_sewa.slice(0,7) : new Date().toISOString().slice(0,7);
    const today = new Date().toISOString().slice(0,10);
    const startDate = reg.tanggal_mulai_sewa ? reg.tanggal_mulai_sewa.slice(0,10) : today;
    setVerifyData({ property_name: '', room_number: reg.nomor_unit || '', event_date: startDate, room_price: '', deposit_amount: '', deposit_date: today, laundry: !!reg.laundry, laundry_price: '', cleaning: !!reg.cleaning, cleaning_price: '', parkir: reg.jenis_kendaraan && reg.jenis_kendaraan !== 'tidak ada', parkir_price: '', rent_payment_date: today });
    if (act === 'verify' && reg.cabang) {
      axios.get(`${BACKEND_URL}/api/staff/properties`, cfg()).then(r => {
        const requested = String(reg.cabang || '').trim().toLowerCase();
        const prop = r.data.find(p => String(p.name || '').trim().toLowerCase() === requested);
        if (!prop) return setRooms([]);
        setVerifyData(d => ({ ...d, property_name: prop.name }));
        axios.get(`${BACKEND_URL}/api/admin/rooms?property_name=${encodeURIComponent(prop.name)}`, cfg()).then(rr => setRooms(rr.data || [])).catch(() => setRooms([]));
      });
    }
  };

  const handleSubmit = async () => {
    if (!canVerify) return;
    setError(''); setSubmitting(true);
    try {
      if (action === 'verify') {
        if (!verifyData.deposit_luar_admin) {
          if (!verifyData.deposit_amount || parseFloat(verifyData.deposit_amount) <= 0)
            return setError('Jumlah deposit wajib diisi (atau centang Di Luar Admin)') || setSubmitting(false);
          if (!verifyData.deposit_date)
            return setError('Tanggal transfer deposit wajib diisi') || setSubmitting(false);
        }
        await axios.put(`${BACKEND_URL}/api/admin/tenant-registrations/${selected.reg_id}/verify`, verifyData, cfg());
        setSuccess('Akun tenant berhasil diaktifkan dan kamar diperbarui');
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/tenant-registrations/${selected.reg_id}/reject`, {}, cfg());
        setSuccess('Pendaftaran ditolak dan datanya telah dihapus');
      }
      setSelected(null); refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || e.response?.data?.message || e.message || 'Gagal'); }
    finally { setSubmitting(false); }
  };

  const filtered = regs.filter(r => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (r.nama || '').toLowerCase().includes(q) ||
      (r.phone || '').includes(q) ||
      (r.cabang || '').toLowerCase().includes(q) ||
      (r.reg_id || '').toLowerCase().includes(q);
  });

  const StatusBadge = ({ s }) => ({
    pending: <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 font-medium">Menunggu</span>,
    verified: <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">Terverifikasi</span>,
  }[s] || null);

  const openDetail = (reg) => {
    setSelected(reg); setAction(''); setError('');
    const defaultPeriod2 = reg.tanggal_mulai_sewa ? reg.tanggal_mulai_sewa.slice(0,7) : new Date().toISOString().slice(0,7);
    const today2 = new Date().toISOString().slice(0,10);
    const startDate2 = reg.tanggal_mulai_sewa ? reg.tanggal_mulai_sewa.slice(0,10) : today2;
    setVerifyData({ property_name: '', room_number: reg.nomor_unit || '', event_date: startDate2, room_price: '', deposit_amount: '', deposit_date: today2, laundry: !!reg.laundry, laundry_price: '', cleaning: !!reg.cleaning, cleaning_price: '', parkir: reg.jenis_kendaraan && reg.jenis_kendaraan !== 'tidak ada', parkir_price: '', rent_payment_date: today2 });
    if (reg.cabang) {
      axios.get(`${BACKEND_URL}/api/staff/properties`, cfg()).then(r => {
        const requested = String(reg.cabang || '').trim().toLowerCase();
        const prop = r.data.find(p => String(p.name || '').trim().toLowerCase() === requested);
        if (!prop) return setRooms([]);
        setVerifyData(d => ({ ...d, property_name: prop.name }));
        axios.get(`${BACKEND_URL}/api/admin/rooms?property_name=${encodeURIComponent(prop.name)}`, cfg()).then(rr => setRooms(rr.data || [])).catch(() => setRooms([]));
      }).catch(() => setRooms([]));
    }
  };

  if (loading) return <div className="p-8 text-center text-isk-muted">Memuat...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
      {success && <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">{success}</div>}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-bold text-isk-text flex items-center gap-2"><UserPlus className="w-5 h-5 text-isk-teal" /> Pendaftaran Tenant</h2>
        <div className="flex gap-1 bg-white/40 rounded-xl p-1">
          {[['all','Semua'],['pending','Menunggu'],['verified','Terverifikasi']].map(([s, label]) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === s ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
              {label}{s !== 'all' && ` (${regs.filter(r => r.status === s).length})`}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-isk-muted" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Cari nama, no. HP, cabang, atau ID pendaftaran..."
          className="w-full pl-9 pr-4 py-2 rounded-full border border-white/40 bg-white/60 text-sm text-isk-text placeholder:text-isk-muted focus:outline-none focus:ring-2 focus:ring-isk-teal/30"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-isk-muted hover:text-isk-text">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Tabel ringkas */}
      <div className="glass-card rounded-2xl overflow-hidden border border-white/40">
        <div className="hidden sm:grid grid-cols-[2fr_1fr_1.5fr_1.5fr_1fr_auto] gap-3 px-4 py-2.5 bg-white/40 border-b border-white/40 text-xs font-semibold text-isk-muted">
          <span>Nama</span><span>No. HP</span><span>Cabang / Unit</span><span>Tgl Daftar</span><span>Status</span><span></span>
        </div>
        {filtered.length === 0 && <div className="py-12 text-center text-isk-muted text-sm">Tidak ada pendaftaran</div>}
        <div className="divide-y divide-white/20">
          {filtered.map(r => (
            <div key={r.reg_id} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.5fr_1.5fr_1fr_auto] gap-2 sm:gap-3 px-4 py-3 items-center hover:bg-white/30 transition-colors">
              <div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="font-semibold text-isk-text text-sm">{r.nama}</p>
                  {parseInt(r.jumlah_penghuni) === 2 && (
                    <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[11px] font-medium">2 Penghuni</span>
                  )}
                </div>
                <p className="text-xs text-isk-muted sm:hidden">{r.phone} · {r.cabang || '-'}</p>
              </div>
              <p className="hidden sm:block text-xs text-isk-muted">{r.phone}</p>
              <p className="hidden sm:block text-xs text-isk-text">{r.cabang || '-'}{r.nomor_unit ? ` · ${r.nomor_unit}` : ''}</p>
              <p className="hidden sm:block text-xs text-isk-muted">{fmtDate(r.created_at)}</p>
              <div className="hidden sm:block"><StatusBadge s={r.status} /></div>
              <Button size="sm" onClick={() => openDetail(r)}
                className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-7 px-3 transition-colors">
                Detail <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Detail Popup */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-xl max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-isk-text text-lg">{selected.nama}</h3>
                <p className="text-xs text-isk-muted mt-0.5">{selected.reg_id} · Daftar {fmtDate(selected.created_at)}</p>
                <div className="mt-1"><StatusBadge s={selected.status} /></div>
              </div>
              <button onClick={() => { setSelected(null); setAction(''); setError(''); }}><X className="w-5 h-5" /></button>
            </div>

            {error && <div className="mb-3 p-2 rounded-lg bg-red-50 text-red-700 text-xs">{error}</div>}

            <div className="space-y-4">
              {/* Data Diri */}
              <RegSection title="Data Diri">
                <RegGrid items={[['No. HP', selected.phone], ['TTL', `${selected.tempat_lahir||'-'}, ${fmtDate(selected.tanggal_lahir)}`], ['Pekerjaan', selected.pekerjaan||'-'], ['Alamat Kerja', selected.alamat_pekerjaan||'-']]} />
                {(selected.sosmed_instagram || selected.sosmed_tiktok) && (
                  <div className="mt-2 p-2 rounded-lg bg-gray-50 text-xs flex gap-4">
                    {selected.sosmed_instagram && <span className="text-isk-muted">Instagram: <span className="font-medium text-isk-text">{selected.sosmed_instagram}</span></span>}
                    {selected.sosmed_tiktok && <span className="text-isk-muted">TikTok: <span className="font-medium text-isk-text">{selected.sosmed_tiktok}</span></span>}
                  </div>
                )}
              </RegSection>
              <RegSection title="Kontak Darurat">
                <RegGrid items={[['Nama', selected.kontak_nama||'-'], ['Hubungan', selected.kontak_hubungan||'-'], ['No. Telp', selected.kontak_telp||'-']]} />
              </RegSection>
              <RegSection title="Data Kamar">
                <RegGrid items={[['Cabang', selected.cabang||'-'], ['Unit', selected.nomor_unit||'-'], ['Mulai Sewa', fmtDate(selected.tanggal_mulai_sewa)], ['Penghuni', selected.jumlah_penghuni||1], ['Laundry', selected.laundry?'Ya':'Tidak'], ['Cleaning', selected.cleaning?'Ya':'Tidak']]} />
              </RegSection>
              {selected.jenis_kendaraan && selected.jenis_kendaraan !== 'tidak ada' && (
                <RegSection title="Kendaraan">
                  <RegGrid items={[['Jenis', selected.jenis_kendaraan], ['No. Plat', selected.nomor_plat||'-']]} />
                </RegSection>
              )}
              {(selected.nama_rekening || selected.rekening_bca || selected.rekening_gopay || selected.rekening_dana) && (
                <RegSection title="Rekening Pengembalian Deposit">
                  <div className="space-y-1 text-xs">
                    {selected.nama_rekening && <div className="p-2 rounded-lg bg-gray-50"><p className="text-isk-muted">Nama Pemilik</p><p className="font-medium text-isk-text">{selected.nama_rekening}</p></div>}
                    <div className="grid grid-cols-3 gap-2">
                      {selected.rekening_bca && <div className="p-2 rounded-lg bg-blue-50 border border-blue-100"><p className="text-isk-muted">BCA</p><p className="font-medium text-isk-text">{selected.rekening_bca}</p></div>}
                      {selected.rekening_gopay && <div className="p-2 rounded-lg bg-green-50 border border-green-100"><p className="text-isk-muted">GoPay</p><p className="font-medium text-isk-text">{selected.rekening_gopay}</p></div>}
                      {selected.rekening_dana && <div className="p-2 rounded-lg bg-blue-50 border border-blue-100"><p className="text-isk-muted">DANA</p><p className="font-medium text-isk-text">{selected.rekening_dana}</p></div>}
                    </div>
                  </div>
                </RegSection>
              )}
              <RegSection title="Dokumen">
                <div className="grid grid-cols-3 gap-2">
                  {[['KTP', 'ktp_url'], ['Bukti Bayar', 'bukti_pembayaran_url'], ['Bukti Deposit', 'bukti_deposit_url']].map(([label, field]) => (
                    <DocFileCard key={`${selected.reg_id}-${field}`} label={label} field={field} regId={selected.reg_id} token={localStorage.getItem('token')} />
                  ))}
                </div>
              </RegSection>
              <RegSection title="Persetujuan Perjanjian">
                <RegGrid items={[
                  ['Status', selected.agreement_accepted ? 'Sudah disetujui' : 'Belum tersedia'],
                  ['Tanggal', fmtDateTime(selected.agreement_signed_at)],
                  ['Lokasi', selected.agreement_location || '-'],
                ]} />
                {selected.agreement_accepted && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openAgreementPreview(selected)}
                    disabled={loadingAgreement === selected.reg_id}
                    className="mt-2 w-full rounded-full border-isk-teal text-isk-teal hover:bg-isk-teal hover:text-white text-xs"
                  >
                    <Eye className="w-4 h-4 mr-1.5" />
                    {loadingAgreement === selected.reg_id ? 'Memuat preview...' : 'Preview Perjanjian Bertanda Tangan'}
                  </Button>
                )}
              </RegSection>

              {parseInt(selected.jumlah_penghuni) === 2 && selected.penghuni2_nama && (
                <RegSection title="Penghuni Kedua">
                  <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-100 space-y-2">
                    <RegGrid items={[['Nama', selected.penghuni2_nama||'-'], ['No. HP', selected.penghuni2_phone||'-'], ['Status', selected.penghuni2_status||'-']]} />
                    <div className="grid grid-cols-1 mt-2">
                      <DocFileCard key={`${selected.reg_id}-penghuni2_ktp_url`} label="KTP Penghuni 2" field="penghuni2_ktp_url" regId={selected.reg_id} token={localStorage.getItem('token')} />
                    </div>
                  </div>
                </RegSection>
              )}

              {/* Status info */}
              {selected.status === 'verified' && (
                <div className="p-2 rounded-lg bg-green-50 border border-green-200 text-xs text-green-700">
                  Diverifikasi oleh {selected.verified_by||'-'} · {fmtDate(selected.verified_at)} · Properti: {selected.property_name||'-'}
                </div>
              )}
              {/* Aksi untuk pending */}
              {canVerify && selected.status === 'pending' && action === '' && (
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <Button onClick={() => setAction('verify')} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full text-sm">
                    <CheckCircle className="w-4 h-4 mr-1" /> Verifikasi & Aktifkan
                  </Button>
                  <Button variant="outline" onClick={() => setAction('reject')} className="flex-1 border-red-300 text-red-600 hover:bg-red-50 rounded-full text-sm">
                    <XCircle className="w-4 h-4 mr-1" /> Tolak
                  </Button>
                </div>
              )}

              {/* Form verifikasi */}
              {canVerify && action === 'verify' && (
                <div className="space-y-3 p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20">
                  <p className="text-xs font-semibold text-isk-text">Data Kamar Aktual *</p>
                  <div>
                    <Label className="text-xs">Properti *</Label>
                    <Select value={verifyData.property_name} onValueChange={v => { setVerifyData(d => ({...d, property_name: v, room_number: ''})); axios.get(`${BACKEND_URL}/api/admin/rooms?property_name=${encodeURIComponent(v)}`, cfg()).then(r => setRooms(r.data||[])).catch(() => setRooms([])); }}>
                      <SelectTrigger className="mt-1 rounded-xl h-8 text-xs"><SelectValue placeholder="Pilih properti" /></SelectTrigger>
                      <SelectContent>{properties.map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Nomor Kamar *</Label>
                    {rooms.length > 0 ? (
                      <Select value={verifyData.room_number} onValueChange={v => setVerifyData(d => ({...d, room_number: v}))}>
                        <SelectTrigger className="mt-1 rounded-xl h-8 text-xs"><SelectValue placeholder="Pilih kamar" /></SelectTrigger>
                        <SelectContent>{rooms.filter(r => r.status === 'available').map(r => <SelectItem key={r.room_number} value={r.room_number}>{r.room_number} ({r.room_type||'Standard'})</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <Input value={verifyData.room_number} onChange={e => setVerifyData(d => ({...d, room_number: e.target.value}))} placeholder="Nomor kamar" className="mt-1 rounded-xl h-8 text-xs" />}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Tgl Mulai Sewa</Label><Input type="date" value={verifyData.event_date} onChange={e => setVerifyData(d => ({...d, event_date: e.target.value}))} className="mt-1 rounded-xl h-8 text-xs" /></div>
                    <div><Label className="text-xs">Harga Sewa/Bulan</Label><Input type="text" inputMode="numeric" value={fmtN(verifyData.room_price)} onChange={e => setVerifyData(d => ({...d, room_price: rawN(e.target.value)}))} placeholder="0" className="mt-1 rounded-xl h-8 text-xs" /></div>
                    <div><Label className="text-xs">Tgl Bayar Sewa</Label><Input type="date" value={verifyData.rent_payment_date} onChange={e => setVerifyData(d => ({...d, rent_payment_date: e.target.value}))} className="mt-1 rounded-xl h-8 text-xs" /></div>
                  </div>
                  {/* Fasilitas */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-isk-text">Fasilitas Tambahan</p>
                    {[
                      { key: 'laundry', priceKey: 'laundry_price', label: 'Laundry' },
                      { key: 'cleaning', priceKey: 'cleaning_price', label: 'Kebersihan Kamar' },
                      { key: 'parkir', priceKey: 'parkir_price', label: 'Parkir Kendaraan' },
                    ].map(({ key, priceKey, label }) => (
                      <div key={key} className={`rounded-xl border-2 overflow-hidden ${verifyData[key] ? 'border-isk-teal' : 'border-gray-200'}`}>
                        <label className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs ${verifyData[key] ? 'bg-isk-teal/10 text-isk-teal font-medium' : 'bg-white/60 text-isk-muted'}`}>
                          <input type="checkbox" checked={!!verifyData[key]} onChange={e => setVerifyData(d => ({...d, [key]: e.target.checked}))} className="accent-isk-teal w-3.5 h-3.5" />
                          {label}
                        </label>
                        {verifyData[key] && (
                          <div className="px-3 pb-2 bg-isk-teal/5">
                            <Input type="text" inputMode="numeric" value={fmtN(verifyData[priceKey])} onChange={e => setVerifyData(d => ({...d, [priceKey]: rawN(e.target.value)}))} placeholder={`Harga ${label}/bulan`} className="mt-1 rounded-lg h-7 text-xs" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="p-3 rounded-xl bg-isk-gold/10 border border-isk-gold/20 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-isk-text">Deposit {!verifyData.deposit_luar_admin && <span className="text-red-500">*</span>}</p>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={!!verifyData.deposit_luar_admin} onChange={e => setVerifyData(d => ({...d, deposit_luar_admin: e.target.checked, deposit_amount: '', deposit_date: ''}))} className="accent-isk-teal w-3.5 h-3.5" />
                        <span className="text-xs text-isk-muted font-medium">Di Luar Admin</span>
                      </label>
                    </div>
                    {verifyData.deposit_luar_admin ? (
                      <p className="text-xs text-orange-600 italic">Deposit dilakukan di luar sistem admin. Tidak perlu mengisi nominal.</p>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="text-xs">Nilai Deposit *</Label><Input type="text" inputMode="numeric" value={fmtN(verifyData.deposit_amount)} onChange={e => setVerifyData(d => ({...d, deposit_amount: rawN(e.target.value)}))} placeholder="0" className="mt-1 rounded-xl h-8 text-xs" /></div>
                          <div><Label className="text-xs">Tanggal Transaksi *</Label><Input type="date" value={verifyData.deposit_date} onChange={e => setVerifyData(d => ({...d, deposit_date: e.target.value}))} className="mt-1 rounded-xl h-8 text-xs" /></div>
                        </div>
                        <p className="text-xs text-isk-muted">Akan otomatis dicatat sebagai transaksi pemasukan deposit.</p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setAction('')} className="flex-1 rounded-full text-xs">Batal</Button>
                    <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full text-xs">{submitting ? 'Menyimpan...' : 'Aktifkan Akun'}</Button>
                  </div>
                </div>
              )}

              {/* Form tolak */}
              {canVerify && action === 'reject' && (
                <div className="space-y-2">
                  <p className="text-sm text-red-700">Pendaftaran ini akan dihapus permanen dari database.</p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setAction('')} className="flex-1 rounded-full text-xs">Batal</Button>
                    <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full text-xs">{submitting ? 'Menghapus...' : 'Tolak & Hapus'}</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {agreementPreview && (
        <div className="fixed inset-0 bg-black/70 z-[220] flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[94vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="font-bold text-isk-text text-sm sm:text-base">Perjanjian Bertanda Tangan</h3>
                <p className="text-xs text-isk-muted truncate">{agreementPreview.tenantName} · {agreementPreview.regId}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button type="button" onClick={downloadAgreement} className="bg-isk-teal hover:bg-isk-dark text-white rounded-full h-8 px-3 text-xs">
                  <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
                </Button>
                <button type="button" onClick={closeAgreementPreview} aria-label="Tutup preview" className="p-2 rounded-full text-isk-muted hover:bg-gray-100 hover:text-isk-text">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <iframe
              src={agreementPreview.url}
              title={`Preview perjanjian ${agreementPreview.tenantName}`}
              className="w-full flex-1 bg-gray-100 border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const RegSection = ({ title, children }) => (
  <div><p className="text-xs font-bold text-isk-muted uppercase tracking-wide mb-2">{title}</p>{children}</div>
);
const RegGrid = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
    {items.map(([label, val]) => (
      <div key={label} className="p-2 rounded-lg bg-gray-50 text-xs">
        <p className="text-isk-muted">{label}</p>
        <p className="font-medium text-isk-text">{val}</p>
      </div>
    ))}
  </div>
);

export default AdminPanel;
